import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SensorsIcon from '@mui/icons-material/Sensors';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormHelperText,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import MDInput from 'components/MDInput';
import IconButton from '@mui/material/IconButton';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { SvgIcon } from '@mui/material';
import { ReactComponent as LockIcon } from 'assets/images/icons/lock-icon.svg';
import { ReactComponent as LockOpenIconSvg } from 'assets/images/icons/lock-open-icon.svg';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import LiveClock from 'components/LiveClock/LiveClock.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { checkManualControlPassword, fetchCurrentData, setControllerSetPoints } from 'stores/sensor/controllersSlice';
import { brandColors } from 'types/colors';
import { ControllerCurrentDataFields } from 'types/ControllerFields.js';
import AlertDialogSlide from '../../components/Dialog/AlertDialogSlide';
import CustomSnackbar from '../../components/CustomSnackbar';
import GenericModal from '../../components/GenericModal';
import MDBox from '../../components/MDBox/index';
import ComfortSelector from './components/ComfortSelector';
import ControlModeSwitcher from './components/ControlModeSwitcher';
import DcvMode from './components/DcvMode';
import DelayedNumberInput from './components/DelayedNumberInput';
import HvacUnitSelector from './components/HvacUnitSelector';
import Loading from './components/Loading.js';
import ModeSelector from './components/ModeSelector';
import ProgressBar from './components/ProgressBar';
import Toggle from './components/Toggle';
import VentilationLevelSelector from './components/VentilationLevelSelector';
import { titleStyle } from './styles/style.js';
import { width } from '@mui/system';

const MDBoxCss = {
  width: {
    xs: '100%',
    sm: '20rem',
  },
  // border: `1px solid red`,
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '1rem',
  marginRight: {
    xs: '1rem',
    lg: '0',
  },
  gap: { xs: 1, sm: 2 },
};

const rowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: {
    xs: '100%',
  },
  height: '2.4rem',
  // marginBottom: '1rem',
  gap: 1,
};

const label_style = {
  fontSize: { xs: '.8rem', sm: '.9rem' },
  fontWeight: '600',
};

const rowLabelStyle = {
  ...titleStyle,
  flex: '1 1 auto',
  minWidth: 0,
};

const rowValueBoxStyle = {
  ...titleStyle,
  paddingLeft: 0,
  width: '8.7rem',
  flex: '0 0 8.7rem',
  ml: 1.5,
  border: `1px solid ${brandColors.GREY_DARK}`,
  alignItems: 'center',
  justifyContent: 'center',
};

function AdminOnlyBadge({ title }) {
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '1rem',
          height: '1rem',
          ml: 0.5,
          borderRadius: '50%',
          backgroundColor: brandColors.BLUE_LIGHT,
          color: brandColors.BLUE,
          flex: '0 0 auto',
        }}>
        <AdminPanelSettingsIcon sx={{ fontSize: '.8rem' }} />
      </Box>
    </Tooltip>
  );
}

function ManualControlPassword({ open, onSuccess, onCancel }) {
  const dispatch = useDispatch();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!open) {
      setPassword('');
      setError('');
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async e => {
    e?.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await dispatch(checkManualControlPassword({ password })).unwrap();
      // success -> close the modal
      onSuccess?.();
    } catch (err) {
      // failure -> keep modal open and show message
      setError(typeof err === 'string' ? err : err?.message || 'Invalid password');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2} sx={{ minWidth: 320 }}>
        <MDInput
          value={password}
          name="password"
          type={show ? 'text' : 'password'}
          label="Password"
          onChange={e => setPassword(e.target.value)}
          fullWidth
          required
          // If MDInput wraps MUI TextField/Input, pass adornment via InputProps:
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={show ? 'Hide password' : 'Show password'}
                  onClick={() => setShow(s => !s)}
                  onMouseDown={e => e.preventDefault()} // keep focus
                  edge="end">
                  {show ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {error ? (
          <FormHelperText sx={{ fontWeight: '600' }} error>
            {error}
          </FormHelperText>
        ) : null}

        <Button
          type="submit"
          variant="contained"
          disabled={submitting || !password}
          endIcon={submitting ? <CircularProgress size={18} /> : null}>
          {submitting ? 'Checking…' : 'Confirm'}
        </Button>
      </Stack>
    </form>
  );
}

const UNACTIVE_DEVICE_WINDOW = 60 * 25;
const DEFAULT_COOL_MODE = 'cool';

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
const snapToStep = v => Math.round(v / 10) * 10;
const MODE_LABELS = ['fan', 'cool', 'heat', 'auto'];
const FAN_SPEED_LABELS = ['auto', 'low', 'medium', 'high'];
const normalizeDashboardLabel = (value, labels, fallback) => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[ -]/g, '_');
  const aliases = {
    fan_only: 'fan',
    fanonly: 'fan',
    cooling: 'cool',
    heating: 'heat',
    automatic: 'auto',
    med: 'medium',
    mid: 'medium',
  };
  const label = aliases[normalized] ?? normalized;
  if (labels.includes(label)) return label;
  return fallback;
};
const resolveModeValue = mode => normalizeDashboardLabel(mode, MODE_LABELS, DEFAULT_COOL_MODE);
const resolveFanSpeedValue = fanSpeed => normalizeDashboardLabel(fanSpeed, FAN_SPEED_LABELS, 'low');
const isOnValue = value => value === 1 || value === true;
const parseScheduleObject = value => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return typeof value === 'object' && !Array.isArray(value) ? value : {};
};
const parseScheduleTimeToMinutes = value => {
  if (!value) return null;
  const [hours, minutes] = String(value).split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};
const isWithinCavSchedule = (cavStartTime, cavEndTime, now = new Date()) => {
  const dayKey = String(now.getDay());
  const startSchedule = parseScheduleObject(cavStartTime);
  const endSchedule = parseScheduleObject(cavEndTime);
  if (Object.keys(startSchedule).length === 0 && Object.keys(endSchedule).length === 0) {
    return true;
  }

  const startMinutes = parseScheduleTimeToMinutes(startSchedule[dayKey]);
  const endMinutes = parseScheduleTimeToMinutes(endSchedule[dayKey]);

  if (startMinutes === null || endMinutes === null) return false;
  if (startMinutes === 0 && endMinutes === 0) return true;

  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  if (startMinutes <= endMinutes) {
    return startMinutes <= nowMinutes && nowMinutes <= endMinutes;
  }
  return nowMinutes >= startMinutes || nowMinutes <= endMinutes;
};
const resolveUnitOnOffStatus = (measurements, isRelayModel) => {
  if (!isRelayModel) return measurements.on_off;

  const channel1On = isOnValue(measurements.channel_1);
  const channel2On = isOnValue(measurements.channel_2);
  return channel1On || channel2On ? 1 : 0;
};

export const CurrentData = props => {
  // console.log('CurrentData props:', props);
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [controlOffConfirmationOpen, setControlOffConfirmationOpen] = useState(false);
  const currentUser = useSelector(state => state.sensors.currentUser);
  const isAdminUser = currentUser?.role === 's';

  const currentMeasurements = useSelector(state => state.controllers.currentMeasurements);
  // console.log('currentMeasurements:', currentMeasurements);
  const controlLoading = useSelector(state => state.controllers.controlLoading);
  const lastUpdate = useSelector(state => state.controllers.lastUpdate);

  const didMountRef = useRef(false);

  const isRelayModel = ['RELAY_2CH', 'RELAY_2CH_NC', 'RELAY_4CH', 'RELAY_4CH_NC'].includes(props.controller_model);
  const isHy01AcController = props.controller_model === 'HY01AC';
  const isHy08Ac34Thermostat = props.controller_model === 'HY08AC3-4-Thermostat';

  const [dcvChecked, setDcvChecked] = useState(currentMeasurements.dcv);
  const [controlMode, setControlMode] = useState(currentMeasurements.control);
  const [unitType, setUnitType] = useState(currentMeasurements.unit_type);
  // prettier-ignore
  const [unitOnOffStatus, setUnitOnOffStatus] = useState(
    resolveUnitOnOffStatus(currentMeasurements, isRelayModel),
  );
  const [channel_1, setChannel_1] = useState(currentMeasurements.channel_1);
  const [channel_2, setChannel_2] = useState(currentMeasurements.channel_2);
  const [keypadLock, setKeypadLock] = useState(currentMeasurements.keypad_lock);

  const [setPoint, setSetPoint] = useState(currentMeasurements.temp_set_point);
  // prettier-ignore
  const [selectedFanSpeed, setSelectedFanSpeed] = useState(resolveFanSpeedValue(currentMeasurements.fan_speed));
  const [selectedMode, setSelectedMode] = useState(resolveModeValue(currentMeasurements.mode));
  const [coolWater, setCoolWater] = useState(snapToStep(currentMeasurements.cool_water_valve));
  const [comfortLevel, setComfortLevel] = useState(currentMeasurements.comfort);

  const buildFullSetPointPayload = useCallback(
    controlModeValue => ({
      dcv: dcvChecked,
      controlMode: controlModeValue,
      on_off: Number(unitOnOffStatus),
      channel_1,
      channel_2,
      keypad_lock: keypadLock,
      ...(isAdminUser
        ? {
            temp_set_point: setPoint,
            fan_speed: selectedFanSpeed,
          }
        : {}),
      mode: selectedMode,
      cool_water_valve: coolWater,
      comfort: comfortLevel,
      unit_type: unitType,
    }),
    [
      channel_1,
      channel_2,
      comfortLevel,
      coolWater,
      dcvChecked,
      isAdminUser,
      keypadLock,
      selectedFanSpeed,
      selectedMode,
      setPoint,
      unitOnOffStatus,
      unitType,
    ],
  );

  // console.log(
  //   `currentMeasurements: ${JSON.stringify(currentMeasurements)}, dcvChecked: ${dcvChecked}, control: ${control}, unitOnOffStatus: ${unitOnOffStatus}, setPoint: ${setPoint}, selectedFanSpeed: ${selectedFanSpeed}, selectedMode: ${selectedMode}, coolWater: ${coolWater}`,
  // );
  // console.log(`${new Date().toLocaleString()}:CurrentData rerender!`);
  // console.log('CurrentData rerender');
  useEffect(() => {
    setDcvChecked(currentMeasurements.dcv);
    setControlMode(currentMeasurements.control);
    setUnitOnOffStatus(resolveUnitOnOffStatus(currentMeasurements, isRelayModel));
    setChannel_1(currentMeasurements.channel_1);
    setChannel_2(currentMeasurements.channel_2);
    setKeypadLock(currentMeasurements.keypad_lock);
    setSetPoint(currentMeasurements.temp_set_point);
    setSelectedFanSpeed(resolveFanSpeedValue(currentMeasurements.fan_speed));
    setSelectedMode(resolveModeValue(currentMeasurements.mode));
    setCoolWater(currentMeasurements.cool_water_valve);
    setComfortLevel(currentMeasurements.comfort);
    setUnitType(currentMeasurements.unit_type);
  }, [currentMeasurements, isRelayModel]);

  const displayedValue = useMemo(() => coolWater, [coolWater]);
  const sourceSensorEntries = useMemo(
    () => Object.entries(currentMeasurements.source_sensors ?? {}),
    [currentMeasurements.source_sensors],
  );
  const shouldShowSourceSensors =
    sourceSensorEntries.length > 1 ||
    (sourceSensorEntries.length === 1 && Number(sourceSensorEntries[0][0]) !== Number(props.controller_id));
  const isFanOnlyMode = selectedMode === 'fan';
  const canEditSetPoint = dcvChecked && controlMode === 'manual' && !(isHy01AcController && isFanOnlyMode);
  const hideAutoFanSpeedOption = (isHy01AcController || isHy08Ac34Thermostat) && isFanOnlyMode;

  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [cavScheduleTick, setCavScheduleTick] = useState(0);
  const lastCurrentDataUpdateRef = useRef(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCavScheduleTick(tick => tick + 1);
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  const isCavActiveNow = useMemo(
    () => isWithinCavSchedule(props.cav_start_time, props.cav_end_time),
    [props.cav_start_time, props.cav_end_time, cavScheduleTick],
  );

  useEffect(() => {
    if (isCavActiveNow) return;

    setUnitOnOffStatus(0);
    setChannel_1(0);
    setChannel_2(0);
  }, [isCavActiveNow]);

  const updateCurrentData = () => {
    const now = Date.now();
    if (now - lastCurrentDataUpdateRef.current < 60000) return;
    lastCurrentDataUpdateRef.current = now;
    dispatch(
      fetchCurrentData({
        fields: Object.values(ControllerCurrentDataFields).map(f => f.db_col_name),
        controller_id: props.controller_id,
      }),
    );
  };

  const dispatchSetPointUpdate = useCallback(
    async (changes, successMessage) => {
      const nextPayload = Object.fromEntries(Object.entries(changes).filter(([, value]) => value !== undefined));
      if (Object.keys(nextPayload).length === 0) {
        return;
      }

      try {
        await dispatch(
          setControllerSetPoints({
            controller_id: props.controller_id,
            data: nextPayload,
            device_mac: props.device_mac,
          }),
        ).unwrap();

        setSnack({
          open: true,
          message: successMessage,
          severity: 'success',
        });
        updateCurrentData();
      } catch (err) {
        setSnack({
          open: true,
          message: err.message,
          severity: 'error',
        });
        console.error('Thunk failed:', err.message);
      }
    },
    [dispatch, props.controller_id, props.device_mac],
  );

  useEffect(() => {
    updateCurrentData();

    const updateCurrentDataId = setInterval(updateCurrentData, 300000);

    return () => {
      clearInterval(updateCurrentDataId);
    };
  }, []);

  const updateValue = useCallback(
    delta => {
      if (controlMode !== 'manual' || !unitOnOffStatus) return;
      const next = snapToStep(clamp(coolWater + delta, 0, 100));
      setCoolWater(next);
      dispatchSetPointUpdate({ cool_water_valve: next }, `Changed cool water valve to ${next}%`);
    },
    [controlMode, coolWater, unitOnOffStatus, dispatchSetPointUpdate],
  );

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    // if (dcvChecked) {
    //   setControlMode('solai');
    // }
  }, [dcvChecked]);

  const handleDCVChange = event => {
    const next = event.target.checked;
    if (!next) {
      setControlOffConfirmationOpen(true);
      return;
    }
    setDcvChecked(next);
    if (event.target.checked) {
      setControlMode('solai');
    }
    dispatchSetPointUpdate(
      {
        dcv: next,
        ...(next ? { controlMode: 'solai' } : {}),
      },
      'Control turned ON',
    );
  };

  const handleConfirmControlOff = () => {
    setControlOffConfirmationOpen(false);
    setDcvChecked(false);
    dispatchSetPointUpdate({ dcv: false }, 'Control turned OFF');
  };

  const handlePowerChange = event => {
    if (!isCavActiveNow) return;
    const next = event.target.checked;
    setUnitOnOffStatus(next);
    dispatchSetPointUpdate({ on_off: Number(next) }, `HVAC turned ${next ? 'ON' : 'OFF'}`);
  };

  const handleRelayPowerChange = event => {
    if (!isCavActiveNow) return;
    const next = event.target.checked ? 1 : 0;
    setChannel_1(next);
    setChannel_2(next);
    dispatchSetPointUpdate(
      {
        on_off: next,
        channel_1: next,
        channel_2: next,
      },
      `HVAC turned ${next ? 'ON' : 'OFF'}`,
    );
  };

  const handleKeypadLockChange = event => {
    const next = event.target.checked;
    setKeypadLock(next);
    dispatchSetPointUpdate({ keypad_lock: next }, `Keypad ${next ? 'locked' : 'unlocked'}`);
  };

  const handleControlChange = next => {
    // if (next === 'manual' && control !== 'manual') {
    //   setOpenModal(true);
    //   // setPrevControl(control);
    //   return;
    // }
    // resume();
    const switchingToManual = next === 'manual' && controlMode !== 'manual';
    setControlMode(next);
    dispatchSetPointUpdate(
      switchingToManual ? buildFullSetPointPayload(next) : { controlMode: next },
      `Changed control mode to ${next.toUpperCase()}`,
    );
  };

  // const handleUnitTypeChange = next => {
  //   // if (next === 'AHU' && control !== 'AHU') {
  //   //   return;
  //   // }
  //   // resume();
  //   setUnitType(next);
  // };
  const handleSetPointChange = useCallback(
    value => {
      setSetPoint(value);
      dispatchSetPointUpdate({ temp_set_point: value }, `Changed temperature set point to ${value}°`);
    },
    [dispatchSetPointUpdate],
  );

  const handleFanSpeedSelect = useCallback(
    value => {
      dispatchSetPointUpdate({ fan_speed: value }, `Changed fan speed to ${value.toUpperCase()}`);
    },
    [dispatchSetPointUpdate],
  );

  const handleModeSelect = useCallback(
    value => {
      dispatchSetPointUpdate({ mode: value }, `Changed mode to ${value.toUpperCase()}`);
    },
    [dispatchSetPointUpdate],
  );

  const handleComfortChange = useCallback(
    value => {
      setComfortLevel(value);
      dispatchSetPointUpdate({ comfort: value }, `Changed comfort level to ${value}`);
    },
    [dispatchSetPointUpdate],
  );

  const handleSuccess = async () => {
    setControlMode('manual');
    setOpenModal(false);
    await dispatchSetPointUpdate(buildFullSetPointPayload('manual'), 'Changed control mode to MANUAL');
  };

  const handleCancel = () => {
    // user didn’t enter manual after all
    // resume();
    setOpenModal(false);
  };

  if (controlLoading) {
    return <Loading />;
  }

  let now = Math.floor(Date.now() / 1000);
  const isDeviceActive = now - lastUpdate > UNACTIVE_DEVICE_WINDOW ? false : true;

  if (currentMeasurements.control === null || Object.keys(currentMeasurements).length === 0 || !isDeviceActive) {
    return (
      <Card
        variant="outlined"
        sx={{
          borderColor: 'error.main',
          borderRadius: 2,
          p: 2,
          maxWidth: 320,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.50',
        }}>
        <WarningAmberIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
        <CardContent sx={{ p: 0 }}>
          <Typography variant="subtitle1" color="error.main" fontWeight="bold">
            Control Unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time data is not accessible at the moment. Please check back later.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const renderPowerLikeRow = ({ label, value, onChange, forceDisabled = false }) => {
    const isDisabled = forceDisabled || !isCavActiveNow;
    const showCavHoursTooltip = label === 'Power' && !isCavActiveNow;
    const isOn = !isDisabled && (value === 1 || value === true);
    const isPowerControl = dcvChecked && controlMode === 'manual';

    return (
      <Box sx={rowStyle}>
        <Box sx={rowLabelStyle}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography sx={label_style}>{label}</Typography>
            {showCavHoursTooltip && (
              <Tooltip title="The unit cannot be turned on because the building is currently outside its active hours. Update the building's active hours to enable operation.">
                <HelpOutlineIcon sx={{ scale: 1.4, color: brandColors.GREY_DARK }} />
              </Tooltip>
            )}
          </Stack>
        </Box>
        <Box sx={{ width: '150px', flex: '0 0 150px', ml: 1.5, display: 'flex', justifyContent: 'center' }}>
          {isPowerControl ? (
            <IconButton
              aria-label={`Turn power ${isOn ? 'off' : 'on'}`}
              aria-pressed={isOn}
              disabled={isDisabled}
              onClick={() => onChange({ target: { checked: !isOn } })}
              sx={{
                width: { xs: 26 },
                height: { xs: 26 },
                borderRadius: '50%',
                backgroundColor: isOn ? brandColors.GREEN : brandColors.GREY,
                color: '#fff',
                '&:hover': {
                  backgroundColor: isOn ? brandColors.GREEN : brandColors.GREY,
                },
                '&.Mui-disabled': {
                  backgroundColor: isOn ? brandColors.GREEN : brandColors.GREY,
                  color: '#fff',
                },
              }}>
              <PowerSettingsNewIcon sx={{ fontSize: { xs: 21, sm: 24 } }} />
            </IconButton>
          ) : (
            <Box
              sx={{
                ...rowValueBoxStyle,
                ml: 0,
              }}>
              <Typography sx={label_style}>{isOn ? 'ON' : 'OFF'}</Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderModeRow = (allowedModeKeys = undefined) => {
    const visibleModeKeys = allowedModeKeys ?? ['Cool', 'Heat'];

    return (
      <Box sx={rowStyle}>
        <Box sx={rowLabelStyle}>
          <Typography sx={label_style}>Mode</Typography>
        </Box>
        <ModeSelector
          control={controlMode}
          unitOnOffStatus={unitOnOffStatus}
          selectedMode={selectedMode}
          setSelectedMode={setSelectedMode}
          currMode={selectedMode}
          onManualSelect={handleModeSelect}
          controllerModel={props.controller_model}
          isRelayModel={isRelayModel}
          allowedModeKeys={visibleModeKeys}
        />
      </Box>
    );
  };

  return (
    <Grid
      sx={{
        display: 'flex',
        flexGrow: '1',
        flexDirection: {
          xs: 'column',
        },
        marginTop: {
          xs: 0,
        },
        flexWrap: {
          xs: 'nowrap',
          sm: 'wrap',
          lg: 'nowrap',
        },
        alignItems: 'center',
        alignContent: 'center',
        justifyContent: 'flex-start',
      }}
      container>
      <CustomSnackbar
        {...snack}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        duration={3000}
        vertical="top"
        horizontal="left"
        color="#fff"
      />
      <GenericModal
        open={openModal}
        onClose={handleCancel}
        title="Password for Manual Control"
        Component={ManualControlPassword}
        componentProps={{
          open: openModal,
          onSuccess: handleSuccess,
          onCancel: handleCancel,
        }}
        size={{
          width: 'min(600px, 92vw)',
          maxHeight: '80vh',
        }}
        closeOnBackdrop={true}
        closeOnEsc={true}
      />
      <AlertDialogSlide
        open={controlOffConfirmationOpen}
        title="Return to monitoring only"
        content="Are you sure you want to turn off Solin control?"
        handleClose={() => setControlOffConfirmationOpen(false)}
        handleOk={handleConfirmControlOff}
      />
      <MDBox
        sx={{
          borderRadius: '.5rem',
          width: '100%',
          height: '2.5rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <LiveClock color="#3244E2"></LiveClock>
      </MDBox>
      {props.title && <Typography sx={{ textAlign: 'center', fontSize: '1rem' }}>{props.title}</Typography>}
      <MDBox
        className="currdata3container"
        sx={{
          display: 'flex',
          flexWrap: {
            xs: 'nowrap',
            sm: 'wrap',
          },
          marginTop: {
            xs: '1rem',
          },
          flexDirection: {
            xs: 'column',
            sm: 'row',
            lg: 'column',
          },
          alignContent: 'flex-start',
          justifyContent: {
            xs: 'center',
            lg: 'flex-start',
          },
          width: '100%',
        }}>
        <Grid item>
          <MDBox sx={MDBoxCss}>
            {shouldShowSourceSensors && (
              <Box sx={{ ...rowStyle, height: 'auto', minHeight: '2.4rem' }}>
                <Box
                  sx={{
                    ...titleStyle,
                    width: '14rem',
                    height: 'auto',
                    minHeight: '2.3rem',
                  }}>
                  <Typography sx={label_style}>Collective mode enabled</Typography>
                </Box>

                <Tooltip
                  placement="bottom"
                  title={
                    <Box>
                      {sourceSensorEntries.map(([sensorId, sensorName]) => (
                        <Typography key={sensorId} fontSize={12}>
                          {sensorName}
                        </Typography>
                      ))}
                    </Box>
                  }>
                  <Box
                    sx={{
                      ...titleStyle,
                      paddingLeft: 0,
                      paddingRight: 0,
                      width: '6rem',
                      height: 'auto',
                      minHeight: '2.3rem',
                      // border: `1px solid ${brandColors.GREY_DARK}`,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <Typography
                      sx={{
                        ...label_style,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        width: '100%',
                        maxWidth: '100%',
                      }}>
                      {sourceSensorEntries.length}
                      <SensorsIcon sx={{ fontSize: '1.5rem !important' }} />
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            )}
            <Box sx={rowStyle}>
              <Box sx={rowLabelStyle}>
                <Typography sx={label_style}>Control</Typography>
              </Box>

              <Box
                className="control-value"
                sx={{
                  width: '150px',
                  flex: '0 0 150px',
                  ml: 1.5,
                  pl: 1.5,
                  display: 'flex',
                  justifyContent: 'center',
                }}>
                <Toggle checked={dcvChecked} handleChange={handleDCVChange} />
              </Box>
            </Box>

            <Box sx={rowStyle}>
              <Box sx={rowLabelStyle}>
                <Typography
                  sx={{
                    ...label_style,
                    color: dcvChecked ? '#000' : brandColors.GREY,
                  }}>
                  Control Mode
                </Typography>
              </Box>
              <ControlModeSwitcher dcvEnabled={dcvChecked} setControl={handleControlChange} control={controlMode} />
            </Box>

            <Box sx={rowStyle}>
              <Box sx={rowLabelStyle}>
                <Typography
                  sx={{
                    ...label_style,
                    color: controlMode !== 'manual' ? '#000' : brandColors.GREY,
                  }}>
                  DCV Mode
                </Typography>
              </Box>
              <DcvMode dcvEnabled={dcvChecked} setControl={handleControlChange} dcvMode={controlMode} />
            </Box>

            <Divider
              sx={{
                my: 0,
                borderColor: '#000',
                opacity: 1,
              }}
            />
            {isRelayModel ? (
              <>
                {renderPowerLikeRow({
                  label: 'Power',
                  value: channel_1 === 1 || channel_1 === true || channel_2 === 1 || channel_2 === true,
                  onChange: handleRelayPowerChange,
                })}
                {renderModeRow(['Cool', 'Heat'])}
              </>
            ) : (
              renderPowerLikeRow({
                label: 'Power',
                value: unitOnOffStatus,
                onChange: handlePowerChange,
              })
            )}

            {!isRelayModel && (
              <Box sx={rowStyle}>
                <Box sx={rowLabelStyle}>
                  <Typography sx={label_style}>Keypad Lock</Typography>
                </Box>
                <Box sx={{ width: '150px', flex: '0 0 150px', ml: 1.5, display: 'flex', justifyContent: 'center' }}>
                  {dcvChecked ? (
                    <Toggle
                      checked={keypadLock}
                      handleChange={handleKeypadLockChange}
                      control={controlMode}
                      onContent={<SvgIcon component={LockIcon} inheritViewBox fontSize="medium" />}
                      offContent={<SvgIcon component={LockOpenIconSvg} inheritViewBox fontSize="medium" />}
                    />
                  ) : (
                    <Box
                      sx={{
                        ...rowValueBoxStyle,
                        width: '100%',
                        flex: '0 0 100%',
                        ml: 0,
                      }}>
                      {/* <IconButton sx={{ fontSize: '.9rem', fontWeight: '600' }}> */}
                      {unitOnOffStatus === 1 ? (
                        <SvgIcon component={LockIcon} inheritViewBox fontSize="small" />
                      ) : (
                        <SvgIcon component={LockOpenIconSvg} inheritViewBox fontSize="small" />
                      )}
                      {/* </IconButton> */}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* {isAdminUser && !isRelayModel && !(isHy01AcController && isFanOnlyMode) && (
              <Box sx={rowStyle}>
                <Box sx={rowLabelStyle}>
                  <Typography sx={label_style}>Temp Set Point</Typography>
                  <AdminOnlyBadge title="Admin-only set point control" />
                </Box>
                {canEditSetPoint ? (
                  <DelayedNumberInput
                    min={15}
                    max={30}
                    step={1}
                    defaultValue={setPoint}
                    label="℃"
                    onDelayedChange={val => {}}
                    setPoint={setPoint}
                    setSetPoint={handleSetPointChange}
                  />
                ) : (
                  <Box
                    sx={{
                      ...rowValueBoxStyle,
                      height: '2.3rem',
                    }}>
                    {unitOnOffStatus === 0 ? (
                      <Tooltip title="unit is currently OFF" placement="bottom">
                        <Typography
                          disabled
                          sx={{
                            ...label_style,
                            color: '#969696',
                          }}>
                          {setPoint}℃
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography sx={label_style}>{setPoint}℃</Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {isAdminUser && !isRelayModel && (
              <Box sx={rowStyle}>
                <Box sx={rowLabelStyle}>
                  <Typography sx={label_style}>Fan Speed</Typography>
                  <AdminOnlyBadge title="Admin-only fan speed control" />
                </Box>
                <VentilationLevelSelector
                  fanSpeed={selectedFanSpeed}
                  control={controlMode}
                  unitOnOffStatus={unitOnOffStatus}
                  selectedFanSpeed={selectedFanSpeed}
                  setSelectedFanSpeed={setSelectedFanSpeed}
                  onManualSelect={handleFanSpeedSelect}
                  hideAutoOption={hideAutoFanSpeedOption}
                />
              </Box>
            )} */}

            {!isRelayModel && renderModeRow(controlMode === 'solai' ? ['Cool', 'Heat'] : undefined)}

            {((dcvChecked && (controlMode === 'solai' || controlMode === 'manual')) ||
              (isRelayModel && controlMode === 'manual')) && (
              <Box sx={{ ...rowStyle, width: '100%' }}>
                <Box sx={rowLabelStyle}>
                  <Typography sx={{ ...label_style, whiteSpace: 'nowrap' }}>Comfort Mode</Typography>
                </Box>
                <ComfortSelector value={comfortLevel} onChange={handleComfortChange} />
              </Box>
            )}

            {(isHy01AcController || !isRelayModel) && (
              <Box sx={rowStyle}>
                <Box sx={rowLabelStyle}>
                  <Typography sx={label_style}>Heating Elements</Typography>
                </Box>
                <Box
                  sx={{
                    ...rowValueBoxStyle,
                  }}>
                  {unitOnOffStatus === false ? (
                    <Tooltip title="unit is currently OFF" placement="bottom">
                      <Typography
                        disabled
                        sx={{
                          ...label_style,
                          color: '#969696',
                        }}>
                        {currentMeasurements.heating_elements === 1 ? 'ON' : 'OFF'}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography sx={label_style}>{currentMeasurements.heating_elements === 1 ? 'ON' : 'OFF'}</Typography>
                  )}
                </Box>
              </Box>
            )}

            {!isRelayModel && (
              <Box sx={rowStyle}>
                <Box sx={{ ...rowLabelStyle, backgroundColor: brandColors.BLUE_LIGHT }}>
                  <Typography
                    sx={{
                      ...label_style,
                      color: brandColors.BLUE,
                      // width: '5.5rem',
                    }}>
                    Cool water
                  </Typography>
                </Box>
                <ProgressBar
                  value={displayedValue}
                  dcvChecked={dcvChecked}
                  control={controlMode}
                  unitOnOffStatus={unitOnOffStatus}
                  onChangeDelta={updateValue}
                  color={brandColors.BLUE}
                />
              </Box>
            )}

            {/* {currentUser.role === 's' && (
              <Box sx={rowStyle}>
                <Box
                  sx={{
                    ...titleStyle,
                    width: '9.5rem',
                  }}>
                  <Typography
                  sx={{
                    ...label_style,
                    color: dcvChecked ? '#000' : brandColors.GREY,
                  }}>
                    Unit type
                  </Typography>
                </Box>
                <HvacUnitSelector setUnitType={handleUnitTypeChange} selected={unitType} />
              </Box>
            )} */}
          </MDBox>
        </Grid>
      </MDBox>
      {/* </Draggable> */}
    </Grid>
  );
};
